import { AutogenLayoutOffice401kRates } from "./autogen/AutogenLayoutOffice401kRates";
import {
    Button, ClickEvent, Component, DataSource, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent,
    DataSourceValidationEvent, DataSourceValidationListener, Label, LookupModelSearchEvent, LookupModelSelectionEvent,
    Panel, SaveButton, SelectionMode, Snackbar, Table, TableRow, TableRowDisplayEvent, TableRowExpansionEvent,
    TableSelectionEvent, Textbox, ValidationResult
} from "@mcleod/components";
import { ModelRow, Navigation, UrlUtil } from "@mcleod/core";
import { Office401kSlideout } from "./Office401kSlideout";
import { Office401kRateDetails } from "./Office401kRateDetails";
import { ModelPayee401krateHdr } from "../models/ModelPayee401krateHdr";

export class Office401kRates extends AutogenLayoutOffice401kRates {
    public parentDataSource: DataSource;
    public validationListener: DataSourceValidationListener = (event: DataSourceValidationEvent) => {
        this.validatePayRate(event);
    };
    public rateDetailsMap: Map<string, Office401kRateDetails> = new Map<string, Office401kRateDetails>();
    private payeeSnackbar: Component;

    async onLoad(): Promise<void> {
        this.parentDataSource = this.mainDataSource;
        this.mainDataSource.addAfterModeChangeListener((event: DataSourceModeChangeEvent) => {
            if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
                // Add selection listener to automatically expand rows when selected
                this.tablePayee401kRate.addSelectionListener((event: TableSelectionEvent) => {
                    this.onTableRowSelect(event);
                });
                this.tablePayee401kRate.addRowExpandListener(async (event: TableRowExpansionEvent) => {
                    // populate the details table layout
                    if (event.isExpanding) {
                        const tableRow: TableRow = event.target as TableRow;
                        const expandPanel = event.expandComponentParent as Panel;
                        const layoutRateDetails = expandPanel.findComponentById("layoutRateDetails") as Office401kRateDetails;
                        const uniqueId = tableRow.data.get("unique_id");
                        if (!this.rateDetailsMap.has(uniqueId)) {
                            const rateDetailRows = tableRow.data.get("rate_details", []);
                            if (rateDetailRows) {
                                for (const [key, value] of Object.entries(rateDetailRows)) {
                                    let modelRow: ModelRow;
                                    if (value instanceof ModelRow) {
                                        modelRow = value;
                                        layoutRateDetails.mainDataSource.addRow(modelRow);
                                    } else {
                                        modelRow = new ModelRow(layoutRateDetails.table401kDetails.dataSource.url, false, value);
                                        layoutRateDetails.mainDataSource.addRow(modelRow);
                                    }
                                    modelRow.set("sequence", modelRow.getNumber("sequence") - 1);
                                }
                            }
                            this.rateDetailsMap.set(uniqueId, layoutRateDetails);
                        }
                        layoutRateDetails.initialize(tableRow.data, this.rateDetailsMap.get(uniqueId).sourceDetail401kRate.data);
                    }
                });
                this.tablePayee401kRate.addRowDisplayListener((event: TableRowDisplayEvent) => {
                    const tableRow: TableRow = event.getTableRow();
                    if (tableRow.data._appending) {
                        tableRow.setExpandedAsync(true);
                    }
                    Office401kRates.setTotalsFieldVisibility(tableRow);
                });
                if (event.newMode === DataSourceMode.UPDATE) {
                    this.dataheaderOffice401kRates.showDelete = true;
                }
            } else if (event.newMode === DataSourceMode.SEARCH) {
                this.tablePayee401kRate.expandComponent = null; // Clear the expansion component
                this.tablePayee401kRate.selectionMode = SelectionMode.NONE;
                this.tablePayee401kRate.removeColumn("columnHeaderPayeeContribution");
                this.tablePayee401kRate.removeColumn("columnHeaderEmployerMatch");
            }
        });
        this.mainDataSource.addBeforeExecutionListener((event: DataSourceExecutionEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.ADD) {
                this.sourcePayee401kRate.data.forEach((row: ModelRow) => {
                    row.set("id", this.activeRow.get("id"));
                });
            }
            for (const [key, value] of this.rateDetailsMap) {
                value.sourceDetail401kRate.parentDataSource = this.mainDataSource;
            }
        });
        this.sourcePayee401kRate.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            const saveButton = this.owner?.dataHeader?.["saveButton"] ?? this.dataHeader?.["saveButton"] as SaveButton;
            if (saveButton) {
                saveButton.doOnDataSourceChanged(this.parentDataSource, false);
            }
            this.rateDetailsMap.clear();
        });
        this.mainDataSource.addValidationListener(this.validationListener);
        this.addLayoutDataLoadListener(() => {
            // Handle idToSelect URL parameter to automatically select a rate record
            const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
            if (urlParams && urlParams.idToSelect) {
                this.selectRateRecordById(urlParams.idToSelect);
            }
        });
        this.textboxId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                const selection = event.selection;
                this.mainDataSource.activeRow.set("payee.birth_date", selection.getDate("birth_date"));
                // loop through each rate record and set the "id" field to the payee id
                this.sourcePayee401kRate.data.forEach((row: ModelRow) => {
                    row.set("payee_id", selection.get("id"));
                });
                if (this.mainDataSource.mode !== DataSourceMode.SEARCH) {
                    this.validatePayee()
                }
            }
        });
        this.textboxId.addlValidationCallback = () => {
            const result: ValidationResult = {
                isValid: true,
                component: this.textboxId
            }
            if (this.textboxId.validationWarning) {
                result.isValid = false;
                result.caption = result.validationMessage = this.textboxId.validationWarning;
            }
            return result;
        }
        this.mainDataSource.doBeforeDataSourcePost = async () => {
            if (this.payeeSnackbar) {
                Snackbar.showWarningSnackbar(this.payeeSnackbar);
                return false;
            }
            return true;
        }
        this.buttonExpandAll.addClickListener(() => {
            this.tablePayee401kRate.rows.forEach((row: TableRow) => {
                if (!row.expanded) {
                    row.setExpandedAsync(true);
                }
            });
        });
        this.buttonCollapseAll.addClickListener(() => {
            this.tablePayee401kRate.rows.forEach((row: TableRow) => {
                if (row.expanded) {
                    row.setExpandedAsync(false);
                }
            });
        });
        this.buttonAddPayRate.addClickListener(async () => {
            Office401kSlideout.openSlideout("add", {
                parentDataSource: this.parentDataSource,
                tablePayee401kRate: this.tablePayee401kRate,
                sourcePayee401kRate: this.sourcePayee401kRate,
                rateDetailsMap: this.rateDetailsMap
            });
        });
    }

    public static setTableParentDataSource(table: Table, parentSource: DataSource, modeInbound?: DataSourceMode) {
        if (parentSource != null) {
            table.dataSource.parentDataSource = parentSource;
            const mode = modeInbound ? modeInbound : parentSource.mode;
            if (mode !== DataSourceMode.ADD)
                table.dataSource.mode = mode;
            else
                table.dataSource.setRowsAndMode(mode, []);
        }
    }

    private async onTableRowSelect(event: TableSelectionEvent): Promise<void> {
        // Expand the newly selected row if one is selected
        if (event && event.newRow && event.newRow?.expanded === false) {
            await event.newRow.setExpandedAsync(true);
        }
    }

    rateRowOnClick(event: ClickEvent) {
        // if row is already selected, then selection event will not be triggered
        const row = event.target as TableRow;
        if (row && row.data && !row.expanded && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            row.setExpandedAsync(true);
        } else if (this.mainDataSource.mode !== DataSourceMode.SEARCH && row.expanderButton.imageRotation == 0) {
            row.setExpandedAsync(false).then(() => {
                row.setExpandedAsync(true);
            });
        }
    }

    private rateRowOnDblClick(event: ClickEvent) {
        const row = event.target as TableRow;
        if (row && row.data && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            Office401kSlideout.openSlideout("update", {
                selectedRow: row,
                parentDataSource: this.parentDataSource,
                sourcePayee401kRate: this.sourcePayee401kRate,
                tablePayee401kRate: this.tablePayee401kRate,
                rateDetailsMap: this.rateDetailsMap
            });
        }
    }

    async selectRateRecordById(idToSelect: string): Promise<void> {
        // Find the row that matches the idToSelect parameter
        const targetRow = this.tablePayee401kRate.rows.find(row => {
            return row.data.get("unique_id") === idToSelect;
        });

        if (targetRow) {
            // Select the row
            this.tablePayee401kRate.selectedRow = targetRow;

            // Expand the row if it's not already expanded (this will trigger the existing expansion logic)
            if (!targetRow.expanded) {
                await targetRow.setExpandedAsync(true);
            }
        }
    }

    private async validatePayee(): Promise<void> {
        const lookupRow = await new ModelPayee401krateHdr().searchSingle({id: this.textboxId.getDataValue()});
        if (!lookupRow) {
            this.textboxId.validationWarning = null;
            this.payeeSnackbar = null;
            return;
        }
        const content = new Panel();
        let text: Label;
        if (lookupRow.get("category") === "O") {
            const description = "Payee has an existing Office Payroll 401(k) Rate record that must be updated."
            text = new Label({
                text: description
            });
            const drilldownButton = new Button({
                caption: "Open Record",
                onClick: () => {
                    const url = `${this.layoutName}?mode=update&key=${this.textboxId.getDataValue()}`;
                    Navigation.navigateTo(url, { newTab: true });
                }
            });
            content.add(text);
            content.add(drilldownButton);
            this.textboxId.validationWarning = description;
        }
        else if (lookupRow.get("category") === "D") {
            const description = "Payee has an existing Settlement 401(k) Rate record that cannot be updated from Office Payroll."
            text = new Label({
                text: description
            });
            content.add(text);
            this.textboxId.validationWarning = description;
        }
        this.payeeSnackbar = content;
        Snackbar.showWarningSnackbar(content);
    }

    private validatePayRate(event: DataSourceValidationEvent) {
        if (this.sourcePayee401kRate.data.length === 0) {
            event.validationResults.push({
                validationMessage: "At least one 401(k) Rate must be defined for the payee.",
                caption: "At least one 401(k) Rate must be defined for the payee.",
                component: this,
                isValid: false
            });
        }
    }

    textboxDeductCodeIdBeforeLookupModelSearch(event: LookupModelSearchEvent): void {
        event.filter["code_type"] = "in D,B";
    }

    static setTotalsFieldVisibility(tableRow: TableRow): void {
        const textboxTotalPayeePercent = tableRow.findComponentById("textboxTotalPayeePercent") as Textbox;
        const textboxTotalPayeeAmount = tableRow.findComponentById("textboxTotalPayeeAmount") as Textbox;
        const textboxTotalEmployerPercent = tableRow.findComponentById("textboxTotalEmployerPercent") as Textbox;
        const textboxTotalEmployerAmount = tableRow.findComponentById("textboxTotalEmployerAmount") as Textbox;
        if (textboxTotalPayeePercent && textboxTotalPayeeAmount && textboxTotalEmployerPercent && textboxTotalEmployerAmount) {
            const isFlat = tableRow.data.get("calculation_method") === "F";
            const isPercent = tableRow.data.get("calculation_method") === "P";
            textboxTotalEmployerAmount.visible = isFlat;
            textboxTotalEmployerPercent.visible = isPercent;
            textboxTotalPayeeAmount.visible = isFlat;
            textboxTotalPayeePercent.visible = isPercent;
        }
    }
}
